import { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FolderIcon, DEFAULT_TAG_COLOR } from "@/lib/folderStyle";
import { Tag as TagIcon, ChevronRight } from "lucide-react";

// Build nested tree of folders (each node also lists its direct tags + children)
export function buildFolderTree(folders) {
  const byParent = new Map();
  folders.forEach((f) => {
    const k = f.parent_id || "root";
    if (!byParent.has(k)) byParent.set(k, []);
    byParent.get(k).push(f);
  });
  const make = (parentId) =>
    (byParent.get(parentId) || [])
      .slice()
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name))
      .map((f) => ({ ...f, children: make(f.id) }));
  return make("root");
}

/**
 * TagPicker renders, for each folder, the tags it contains as chips.
 * Folders are collapsed by default. Folders that already contain a selected tag auto-expand
 * so the user can see what's chosen.
 */
export default function TagPicker({
  folders,
  tags,
  selectedTagIds,
  onChange,
  onCreateFolder,
  emptyMessage = "No tag library yet.",
}) {
  const tagsByFolder = useMemo(() => {
    const m = new Map();
    tags.forEach((t) => {
      if (!m.has(t.folder_id)) m.set(t.folder_id, []);
      m.get(t.folder_id).push(t);
    });
    m.forEach((arr) =>
      arr.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name))
    );
    return m;
  }, [tags]);

  const tree = useMemo(() => buildFolderTree(folders), [folders]);
  const selectedSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);

  // Folder ids that contain a currently-selected tag — used to auto-expand on mount/change
  const autoOpenIds = useMemo(() => {
    const s = new Set();
    tags.forEach((t) => {
      if (selectedSet.has(t.id)) s.add(t.folder_id);
    });
    return s;
  }, [tags, selectedSet]);

  // Per-folder expanded state (collapsed by default unless auto-open)
  const [expanded, setExpanded] = useState({});

  // Whenever selection changes (e.g. opening dialog on an existing contact), make sure
  // any folder that contains a selected tag is expanded.
  useEffect(() => {
    if (autoOpenIds.size === 0) return;
    setExpanded((prev) => {
      const next = { ...prev };
      autoOpenIds.forEach((fid) => {
        if (next[fid] === undefined) next[fid] = true;
      });
      return next;
    });
  }, [autoOpenIds]);

  const isOpen = (fid) => expanded[fid] ?? false;
  const toggle = (fid) => setExpanded((p) => ({ ...p, [fid]: !isOpen(fid) }));

  const togglePick = (folder, tag) => {
    const isSelected = selectedSet.has(tag.id);
    onChange(
      isSelected
        ? selectedTagIds.filter((id) => id !== tag.id)
        : [...selectedTagIds, tag.id]
    );
  };

  const renderFolder = (node, depth) => {
    const folderTags = tagsByFolder.get(node.id) || [];
    const open = isOpen(node.id);
    const selectedInside = folderTags.filter((t) => selectedSet.has(t.id)).length;

    return (
      <div key={node.id} className="space-y-2" data-testid={`tagpicker-folder-${node.id}`}>
        <button
          type="button"
          onClick={() => toggle(node.id)}
          className="w-full flex items-center gap-2 group"
          style={{ paddingLeft: depth * 16 }}
          aria-expanded={open}
          data-testid={`tagpicker-folder-toggle-${node.id}`}
        >
          <ChevronRight
            className={`h-3.5 w-3.5 text-white/45 transition-transform ${open ? "rotate-90" : ""}`}
          />
          <span className="inline-flex items-center gap-2" style={{ color: node.color }}>
            <FolderIcon name={node.icon || "Folder"} className="h-3.5 w-3.5" />
            <span className="text-[11px] uppercase tracking-widest font-mono">{node.name}</span>
          </span>
          {selectedInside > 0 && !open && (
            <span
              className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded-full ml-auto"
              style={{
                background: `${node.color}33`,
                color: node.color,
                border: `1px solid ${node.color}AA`,
              }}
            >
              {selectedInside} picked
            </span>
          )}
        </button>

        {open &&
          (folderTags.length === 0 ? (
            <div
              className="text-[11px] font-mono text-white/40 italic"
              style={{ paddingLeft: depth * 16 + 18 }}
            >
              No tags in this folder yet.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" style={{ paddingLeft: depth * 16 + 14 }}>
              {folderTags.map((t) => {
                const sel = selectedSet.has(t.id);
                const color = t.color || node.color || DEFAULT_TAG_COLOR;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => togglePick(node, t)}
                    data-testid={`tagpicker-tag-${t.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-xs font-mono uppercase tracking-widest transition-all"
                    style={
                      sel
                        ? {
                            background: color,
                            color: "#0a0a0a",
                            border: `1px solid ${color}`,
                            boxShadow: `0 0 10px ${color}AA`,
                          }
                        : {
                            background: "transparent",
                            color: `${color}`,
                            border: `1px solid ${color}66`,
                          }
                    }
                  >
                    <TagIcon className="h-3 w-3" />
                    {t.name}
                  </button>
                );
              })}
            </div>
          ))}

        {node.children.length > 0 && (
          <div className="space-y-2">{node.children.map((c) => renderFolder(c, depth + 1))}</div>
        )}
      </div>
    );
  };

  if (tree.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-white/15 p-4 text-center space-y-2">
        <p className="font-mono text-xs text-white/50">{emptyMessage}</p>
        {onCreateFolder && (
          <Button
            type="button"
            onClick={onCreateFolder}
            className="btn-ghost h-9 px-3 text-[#00e5ff] border-[#00e5ff]/40"
            data-testid="tagpicker-create-folder"
          >
            Open Tag Library
          </Button>
        )}
      </div>
    );
  }

  return <div className="space-y-3">{tree.map((n) => renderFolder(n, 0))}</div>;
}
